import { Button } from "@/components/ui/button/Button";
import { Input } from "@/components/ui/input/Input";
import { Label } from "@/components/ui/label/Label";
import { ChangeEvent, FormEvent, useCallback, useEffect, useState } from "react";
import { FaPlus, FaX } from "react-icons/fa6";
import { AnimatePresence, motion } from "framer-motion";
import styles from "./BorrowerData.module.css";
import { Checkbox } from "@/components/ui/checkbox/Checkbox";
import { IMaterialRequestItem, useRoomBorrowingStore } from "@/store/room_borrowing/roomBorrowingStore";
import { cn, resolveBorrowerCredentials } from "@/lib/utils";
import { useToast } from "@/lib/hooks/useToast";
import { useRoomBorrowingNext } from "../RoomBorrowing";
import { useUserStore } from "@/store/user/userStore";
import {
  LOKASI_ANGGREK,
  LOKASI_BEKASI,
  RUANG_LAB_FOODTECH_ALSUT,
  RUANG_LAB_KOMPUTER_ALSUT,
} from "@/lib/constants/constants";
import { UserFacingError } from "@/models/helper/UserFacingError";
import { CircleAlert, Loader2, Search, Trash2 } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useDebounce } from "@/lib/hooks/useDebounce";
import { useInfiniteScroll } from "@/lib/hooks/useInfiniteScroll";
import StockOpnameService from "@/lib/services/borrowing/StockOpnameService";

type Team = {
  id: number;
  value: string;
};

type ItemOption = { id: number; name: string; sizeUnit: string };

const BorrowerData = () => {
  const store = useRoomBorrowingStore();
  const { toast } = useToast();
  const userStore = useUserStore();
  const [members, setMembers] = useState<Team[]>([]);
  const [loading, setLoading] = useState(false);
  const { setRoomBorrowingNext } = useRoomBorrowingNext();
  const [requestedItems, setRequestedItems] = useState<IMaterialRequestItem[]>(
    store.data.requested_materials || []
  );
  const [needChemicals, setNeedChemicals] = useState<boolean>(
    store.data.need_chemicals || false
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [availableItems, setAvailableItems] = useState<ItemOption[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isFetchingItems, setIsFetchingItems] = useState(false);

  const debouncedSearch = useDebounce(searchTerm, 300);

  // Fetch Initial Materials
  useEffect(() => {
    if (!needChemicals) return;
    const fetchInitial = async () => {
      setIsFetchingItems(true);
      setPage(1);
      setHasMore(true);
      try {
        const items = await StockOpnameService.getItems(1, debouncedSearch, store.data.room_code);
        setAvailableItems(items);
        setHasMore(items.length === 20);
      } catch (err) { console.error(err); }
      finally { setIsFetchingItems(false); }
    };
    fetchInitial();
  }, [debouncedSearch, needChemicals, store.data.room_code]);

  // Fetch More Materials (Infinite Scroll)
  useEffect(() => {
    if (page === 1 || !needChemicals) return;
    const fetchMore = async () => {
      setIsFetchingItems(true);
      try {
        const newItems = await StockOpnameService.getItems(page, debouncedSearch, store.data.room_code);
        setAvailableItems((prev) => [...prev, ...newItems]);
        setHasMore(newItems.length === 20);
      } catch (err) { console.error(err); }
      finally { setIsFetchingItems(false); }
    };
    fetchMore();
  }, [page, debouncedSearch, needChemicals, store.data.room_code]);

  const loadMore = useCallback(() => setPage((p) => p + 1), []);
  const lastElementRef = useInfiniteScroll(loadMore, isFetchingItems, hasMore);

  const handleAddMaterial = (item: ItemOption) => {
    if (requestedItems.find((i) => i.itemId === item.id)) return toast({ title: "Info", description: "Item already added." });
    setRequestedItems([...requestedItems, { itemId: item.id, name: item.name, requestAmount: 0, unit: item.sizeUnit }]);
    setSearchTerm("");
    setIsSearchOpen(false);
  };

  const handleRemoveMaterial = (index: number) => setRequestedItems(requestedItems.filter((_, i) => i !== index));

  const handleMaterialAmountChange = (index: number, value: string) => {
    const newItems = [...requestedItems];
    newItems[index].requestAmount = value as unknown as number;
    setRequestedItems(newItems);
  };

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget)) setIsSearchOpen(false);
  };

  useEffect(() => {
    if (store?.data?.team?.length > 0) {
      const formattedMembers = store.data.team.map((teamMember, index) => ({
        id: index + 1,
        value: teamMember.email,
      }));
      setMembers(formattedMembers);
    } else {
      setMembers([]);
    }
  }, [store?.data?.team]);

  const addMember = () => {
    const newId = members.length === 0 ? 1 : members[members.length - 1].id + 1;
    setMembers((prev) => [...prev, { id: newId, value: "" }]);
  };

  const removeMember = (id: number) => {
    setMembers((prev) => prev.filter((v) => v.id !== id));
  };

  const handleInputTeam = (id: number, e: ChangeEvent<HTMLInputElement>) => {
    const idx = members.findIndex((v) => v.id === id);
    const arr = members;
    arr[idx].value = e.target.value;
    setMembers([...arr]);
    setTimeout(() => {
      console.log(members);
    });
  };

  const isSelfLearningType1 =
    store.data.room_type === RUANG_LAB_KOMPUTER_ALSUT &&
    store.data.borrowing_reason_category === "Self Learning";

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setRoomBorrowingNext(false);
    /*
    // BEFORE Self Learning
    if (store.data.phone.trim() === "" || store.data.purpose.trim() === "" || store.data.borrowing_reason_category.trim() === "") {
      toast({
        title: "Form error.",
        description: "Please fill all required fields...",
      });

      return;
    } else if (isNaN(+store.data.phone)) {
      toast({
        title: "Form error.",
        description: "Please fill phone number correctly...",
      });

      return;
    } else if (needChemicals) {
      if (requestedItems.length === 0) {
        return toast({ title: "Form error.", description: "Please add at least one material to your request." });
      }
      if (requestedItems.some(i => i.requestAmount <= 0)) {
        return toast({ title: "Form error.", description: "Material amounts must be greater than zero." });
      }
    }
    */
    if (store.data.phone.trim() === "" || store.data.purpose.trim() === "" || store.data.borrowing_reason_category.trim() === "") {
      toast({
        title: "Form error",
        description: "Please fill all required fields...",
      });

      return;
    } else if (isNaN(+store.data.phone)) {
      toast({
        title: "Form error.",
        description: "Please fill phone number correctly...",
      });

      return;
    } else if (isSelfLearningType1 && store.data.shift.some((s) => +s.id === 6)) {
      toast({
        title: "Form error",
        description: "Self Learning for Computer Laboratory is only available for Shifts 1-5.",
      });

      return;
    } else if (needChemicals) {
      if (requestedItems.length === 0) {
        return toast({ title: "Form error", description: "Please add at least one material to your request." });
      }
      if (requestedItems.some(i => i.requestAmount <= 0)) {
        return toast({ title: "Form error", description: "Material amounts must be greater than zero." });
      }
    }

    setLoading(true);
    try {
      const team = await resolveBorrowerCredentials(
        members.map((m) => m.value)
      );

      store.data.team = team;
      store.update("need_chemicals", needChemicals);

      if (needChemicals) {
        const sanitizedItems = requestedItems.map(item => ({
          ...item,
          requestAmount: parseFloat(String(item.requestAmount)) || 0
        }));
        store.update("requested_materials", sanitizedItems);
      } else {
        store.update("requested_materials", []);
      }

      toast({
        title: "User data successfully saved.",
        description: "Please proceed to next step..",
      });
      setRoomBorrowingNext(true);
    } catch (error) {
      if (error instanceof UserFacingError) {
        toast({ title: error.title, description: error.description });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>

      <div className={styles.inputContainer}>
        <Label>
          Borrowing Reason Category <span className="text-red-500 text-lg">*</span>
        </Label>

        {/*
        // BEFORE Self Learning
        <select
          className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          value={store.data.borrowing_reason_category}
          onChange={(e) => store.update("borrowing_reason_category", e.target.value)}
        >
          <option value="">Select Borrowing Reason Category</option>
          <option value="Extra class / Make Up Class">Extra class / Make Up Class</option>
          <option value="Event">Event</option>
          <option value="Meeting">Meeting</option>
          <option value="Self Learning">Self Learning</option>
          <option value="Seminar">Seminar</option>
          <option value="Training / Workshop">Training / Workshop</option>
          <option value="Other">Other</option>
        </select>
        */}
        <select
          className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          value={store.data.borrowing_reason_category}
          onChange={(e) => {
            const newCategory = e.target.value;
            store.update("borrowing_reason_category", newCategory);
            if (newCategory === "Self Learning" && store.data.room_type === RUANG_LAB_KOMPUTER_ALSUT) {
              store.update("is_private", false);
            }
          }}
        >
          <option value="">Select Borrowing Reason Category</option>
          <option value="Extra class / Make Up Class">Extra class / Make Up Class</option>
          <option value="Event">Event</option>
          <option value="Meeting">Meeting</option>
          <option value="Self Learning">Self Learning</option>
          <option value="Seminar">Seminar</option>
          <option value="Training / Workshop">Training / Workshop</option>
          <option value="Other">Other</option>
        </select>
      </div>

      <div className={styles.inputContainer}>
        <Label>
          Notes <span className="text-red-500 text-lg">*</span>
        </Label>
        <Input
          placeholder="Borrowing Notes"
          name="purpose"
          id="purpose"
          value={store.data.purpose}
          onChange={(e) => store.update("purpose", e.target.value)}
        />
      </div>
      <div className={styles.inputContainer}>
        <Label>
          Phone <span className="text-red-500 text-lg">*</span>
        </Label>
        <Input
          placeholder="Phone Number"
          name="phone"
          id="phone"
          value={store.data.phone}
          onChange={(e) => store.update("phone", e.target.value)}
        />
      </div>
      {/* computer lab alsut atau di lokasinya anggrek/bekasi */}
      {(store?.data?.room_type === RUANG_LAB_KOMPUTER_ALSUT ||
        store?.data?.location === LOKASI_ANGGREK ||
        store?.data?.location === LOKASI_BEKASI) && (
          <>
            <div className={styles.inputContainer}>
              <Label>Do you need internet?</Label>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="internet"
                  name="internet"
                  checked={store.data.need_internet ?? false}
                  onCheckedChange={(checked) =>
                    store.update("need_internet", checked)
                  }
                />
                <label
                  htmlFor="internet"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Yes, I do.
                </label>
              </div>
            </div>

            <div className={styles.inputContainer}>
              <Label>Do you need computer?</Label>
              <div className="flex flex-col space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="computer"
                    name="computer"
                    checked={store.data.need_computer ?? false}
                    onCheckedChange={(checked) => {
                      store.update("need_computer", checked);
                      if (!checked) store.update("pc_count", 0);
                    }}
                  />
                  <label
                    htmlFor="computer"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Yes, I do.
                  </label>
                </div>

                {store.data.need_computer && (
                  <div className="w-full">
                    <Label className="text-sm text-gray-600">How many computers?</Label>
                    <Input
                      type="number"
                      min={1}
                      placeholder="Enter PC count"
                      value={store.data.pc_count || ""}
                      onChange={(e) => store.update("pc_count", parseInt(e.target.value) || 0)}
                      className="mt-1"
                    />
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      {store?.data?.room_type === RUANG_LAB_FOODTECH_ALSUT && (
        <div className={styles.inputContainer}>
          <Label>Do you need to request material?</Label>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="chemicals"
              name="chemicals"
              checked={needChemicals}
              onCheckedChange={(checked) => setNeedChemicals(checked as boolean)}
            />
            <label htmlFor="chemicals" className="text-sm font-medium leading-none">
              Yes, I do.
            </label>
          </div>

          {needChemicals && (
            <div className="mt-6 flex flex-col w-full gap-4">
              <div className="relative w-full" onBlur={handleBlur}>
                <Label className="mb-2 block">
                  Search & Add Materials <span className="text-red-500 text-lg">*</span>
                </Label>

                <div className="relative flex items-center">
                  <Search className="absolute left-3 text-muted-foreground" size={16} />
                  <Input
                    type="text"
                    placeholder="Search for a material..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setIsSearchOpen(true);
                    }}
                    onFocus={() => setIsSearchOpen(true)}
                    onClick={() => setIsSearchOpen(true)}
                    className="pl-9 w-full"
                  />
                </div>

                {isSearchOpen && (
                  <div className="absolute top-full z-50 mt-1 max-h-48 w-full overflow-y-auto rounded-md border bg-background shadow-lg">
                    {availableItems.length === 0 && !isFetchingItems ? (
                      <div className="px-4 py-3 text-sm text-muted-foreground">No materials found.</div>
                    ) : (
                      availableItems.map((item, index) => {
                        const isLastElement = availableItems.length === index + 1;
                        return (
                          <button
                            key={item.id}
                            ref={isLastElement ? lastElementRef : null}
                            type="button"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              handleAddMaterial(item);
                            }}
                            className="flex w-full items-center justify-between px-4 py-3 text-left text-sm hover:bg-accent hover:text-accent-foreground transition-colors"
                          >
                            <span className="font-medium">{item.name}</span>
                            <span className="text-xs opacity-70">({item.sizeUnit})</span>
                          </button>
                        );
                      })
                    )}
                    {isFetchingItems && (
                      <div className="flex justify-center py-3">
                        <Loader2 className="animate-spin text-muted-foreground" size={18} />
                      </div>
                    )}
                  </div>
                )}
              </div>

              {requestedItems.length > 0 && (
                <div className="space-y-3 mt-2 w-full">
                  {requestedItems.map((reqItem, index) => (
                    <div key={index} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-md border bg-background px-4 py-3 shadow-sm">
                      <span className="flex-1 text-sm font-medium">{reqItem.name}</span>

                      <div className="flex items-center gap-3 w-full sm:w-auto">
                        <div className="relative w-32 sm:w-48">
                          <Input
                            type="number"
                            step="any"
                            inputMode="decimal"
                            name={`requestAmount-${index}`}
                            value={reqItem.requestAmount}
                            onChange={(e) => handleMaterialAmountChange(index, e.target.value)}
                            className="pr-12 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-medium text-muted-foreground uppercase pointer-events-none">
                            {reqItem.unit}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveMaterial(index)}
                          className="flex justify-center w-10 text-red-500 hover:text-red-700 p-2 transition-colors"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {/* if the room privacy is mixed (can be private/sharing), and all the chosen shift not yet booked */}
      {/* show the option to book private */}
      {/*
      // BEFORE Self Learning
      {store.data.room_privacy_category === "Mixed" &&
        store.data.shift.every((s) => s.borrower_count === 0) && (
          <div className={styles.inputContainer}>
            <div className="flex flex-row gap-2 items-center">
              <Label>Do you need to borrow whole room?</Label>
              <TooltipProvider delayDuration={50}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <CircleAlert
                      size={"1rem"}
                      className="cursor-pointer text-muted-foreground"
                    />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-96">
                      Select this for exclusive use of the room (e.g., for an
                      extra class or private use). If unchecked, other groups
                      can borrow the remaining capacity at the same time.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="private"
                name="private"
                checked={store.data.is_private ?? false}
                onCheckedChange={(checked) =>
                  store.update("is_private", checked)
                }
              />
              <label
                htmlFor="private"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Yes, I do.
              </label>
            </div>
          </div>
        )}
      */}
      {store.data.room_privacy_category === "Mixed" &&
        store.data.shift.every((s) => s.borrower_count === 0) && (
          <div className={styles.inputContainer}>
            <div className="flex flex-row gap-2 items-center">
              <Label className={isSelfLearningType1 ? "text-muted-foreground" : ""}>
                Do you need to borrow whole room?
              </Label>
              <TooltipProvider delayDuration={50}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <CircleAlert
                      size={"1rem"}
                      className="cursor-pointer text-muted-foreground"
                    />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-96">
                      {isSelfLearningType1
                        ? "Private borrowing is disabled for Self Learning in Computer Laboratory."
                        : "Select this for exclusive use of the room (e.g., for an extra class or private use). If unchecked, other groups can borrow the remaining capacity at the same time."}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="private"
                name="private"
                disabled={isSelfLearningType1}
                checked={isSelfLearningType1 ? false : (store.data.is_private ?? false)}
                onCheckedChange={(checked) => {
                  if (!isSelfLearningType1) {
                    store.update("is_private", checked);
                  }
                }}
              />
              <label
                htmlFor="private"
                className={cn(
                  "text-sm font-medium leading-none",
                  isSelfLearningType1
                    ? "text-muted-foreground cursor-not-allowed opacity-50"
                    : "peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                )}
              >
                Yes, I do.
              </label>
              {isSelfLearningType1 && (
                <span className="text-xs text-muted-foreground italic ml-2">
                  (Disabled for Self Learning in Computer Lab)
                </span>
              )}
            </div>
          </div>
        )}

      <div className={cn(styles.inputContainer, "pb-6")}>
        <div className="flex w-full gap-4 py-2 items-end">
          <div className="w-full">
            <Label className="text-lg text-gray-600">Leader</Label>
            <Input disabled={true} value={`${userStore.data?.Email}`} />
          </div>
          <Button
            type="button"
            onClick={addMember}
            variant={"blue"}
            className="w-60 flex items-center gap-4"
            disabled={loading}
          >
            Add Member <FaPlus />
          </Button>
        </div>
        <div className="flex flex-col w-full gap-2 max-h-48 overflow-y-auto px-6 pb-2">
          <AnimatePresence>
            {members.map((m, idx) => (
              <motion.div
                key={idx}
                className="w-full"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{
                  type: "just",
                  ease: "linear",
                  duration: 0.5,
                }}
              >
                <Label htmlFor={`team${m.id}`}>Member {idx + 1}</Label>
                <div className="flex w-full items-center space-x-2">
                  <Input
                    id={`team${m.id}`}
                    placeholder="Member Email / Binusian ID"
                    className="flex-1"
                    onChange={(e) => handleInputTeam(m.id, e)}
                    value={m.value}
                  />
                  <button
                    className="w-auto"
                    type="button"
                    title="Remove Member"
                  >
                    <FaX
                      onClick={() => removeMember(m.id)}
                      className="text-red-600 hover:text-red-600/75 cursor-pointer transition-colors ease-linear duration-200"
                    />
                  </button>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
      <div className="pt-4">
        <Button
          type="submit"
          variant={"blue"}
          disabled={
            loading ||
            !store.data.purpose.trim() ||
            !store.data.phone.trim() ||
            (needChemicals && (
              requestedItems.length === 0 ||
              requestedItems.some(item => !item.requestAmount || Number(item.requestAmount) <= 0)
            ))
          }
        >
          {loading ? "Saving..." : "Save Data"}
        </Button>
      </div>
    </form>
  );
};

export default BorrowerData;
